import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Euro, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtData = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '-');

const scadenzaInfo = (f) => {
  if (f.giorni_rimanenti === null || f.giorni_rimanenti === undefined) {
    return { label: 'senza scadenza', cls: 'text-muted-foreground border-border' };
  }
  if (f.giorni_rimanenti < 0) {
    return { label: `scaduta da ${Math.abs(f.giorni_rimanenti)} gg`, cls: 'text-red-600 border-red-500 bg-red-50 dark:bg-red-950/30 font-semibold' };
  }
  if (f.giorni_rimanenti === 0) {
    return { label: 'scade oggi', cls: 'text-red-600 border-red-500 font-semibold' };
  }
  if (f.giorni_rimanenti <= 7) {
    return { label: `tra ${f.giorni_rimanenti} gg`, cls: 'text-amber-600 border-amber-500' };
  }
  return { label: fmtData(f.scadenza), cls: 'text-muted-foreground border-border' };
};

/** Widget Dashboard: fatture non pagate ordinate per scadenza, con azione rapida "segna pagata". */
export const WidgetScadenzeIncasso = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);

  const canSegnaPagata = isAdmin() || !!user?.permessi_documenti?.fattura;

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/fatture-scadenze`);
      setData(res.data);
    } catch (e) {
      console.error('Errore scadenze incasso:', e);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const segnaPagata = async (f, e) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/documenti/${f.id}/pagato`, { pagato: true });
      toast.success(`Fattura ${f.anno}/${String(f.numero).padStart(3, '0')} segnata come PAGATA`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento pagamento');
    }
  };

  if (!data || data.fatture.length === 0) return null;

  return (
    <Card data-testid="widget-scadenze-incasso">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-mono uppercase flex items-center gap-2 flex-wrap cursor-pointer hover:text-primary transition-colors" onClick={() => navigate('/documenti?nonpagate=1')} title="Vedi tutte le fatture non pagate" data-testid="incasso-titolo-link">
          <Euro className="h-4 w-4 text-primary" /> Fatture da incassare
          <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-500" data-testid="incasso-totale">
            € {data.totale_da_incassare.toFixed(2)} da incassare
          </Badge>
          {data.scadute > 0 && (
            <Badge className="text-[10px] bg-red-600 text-white animate-pulse" data-testid="incasso-scadute">
              <AlertTriangle className="h-3 w-3 mr-1" /> {data.scadute} scadut{data.scadute === 1 ? 'a' : 'e'}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {data.fatture.slice(0, 8).map((f) => {
          const info = scadenzaInfo(f);
          return (
            <div
              key={f.id}
              className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => navigate('/documenti?nonpagate=1')}
              data-testid={`incasso-riga-${f.id}`}
            >
              <span className="font-mono text-xs font-bold shrink-0">{f.anno}/{String(f.numero).padStart(3, '0')}</span>
              <span className="text-xs truncate flex-1">{f.cliente || '-'}</span>
              <span className="font-mono text-xs font-medium text-emerald-600 shrink-0">€ {f.totale.toFixed(2)}</span>
              <Badge variant="outline" className={`text-[10px] shrink-0 ${info.cls}`} title={f.scadenza ? `Scadenza: ${fmtData(f.scadenza)}` : 'Scadenza non impostata sulla fattura'}>
                {info.label}
              </Badge>
              {canSegnaPagata && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                  title="Segna come PAGATA"
                  onClick={(e) => segnaPagata(f, e)}
                  data-testid={`incasso-paga-${f.id}`}
                >
                  <CheckCircle2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        })}
        {data.fatture.length > 8 && (
          <p className="text-[11px] text-muted-foreground text-right cursor-pointer hover:underline" onClick={() => navigate('/documenti?nonpagate=1')}>
            + altre {data.fatture.length - 8} fatture da incassare — vedi Documenti
          </p>
        )}
      </CardContent>
    </Card>
  );
};
